import { StarIcon } from "@heroicons/react/24/solid";
import { FC, useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import "./CommentListing.scss";

interface CommentListingDataType {
  name: string;
  avatar?: string;
  date: string;
  comment: string;
  starPoint: number;
}

export interface CommentListingProps {
  className?: string;
  hasListingTitle?: boolean;
}

const DEMO_DATA: CommentListingDataType[] = [
  {
    name: "Esther",
    date: "",
    comment:
      "“El martes fuimos toda la familia a celebrar el cumpleaños de una hija. El lugar y la decoración es espectacular, cómo estar en las Vegas con el Rat Pack ...”",
    starPoint: 5,
  },
  {
    name: "Amelia",
    date: "",
    comment: "“Fuimos aquí a celebrar mi cumpleaños y no puedo estar más contenta con el trato que hemos recibido.”",
    starPoint: 5,
  },
  {
    name: "Daniel",
    date: "",
    comment: "“Totalmente recomendable. Fuimos un grupo de 12 personas y todo genial. La atención prestada de 10. Catálogo extenso de canciones. Volveremos seguro”",
    starPoint: 5,
  },
];

const AUTOPLAY_MS = 3000;

const CommentListing: FC<CommentListingProps> = ({ className = "" }) => {
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const [paused, setPaused] = useState(false);

  const goTo = (i: number) => {
    setDirection(i > index ? 1 : -1);
    setIndex((i + DEMO_DATA.length) % DEMO_DATA.length);
  };
  const next = () => {
    setDirection(1);
    setIndex((p) => (p + 1) % DEMO_DATA.length);
  };
  const prev = () => {
    setDirection(-1);
    setIndex((p) => (p - 1 + DEMO_DATA.length) % DEMO_DATA.length);
  };

  // Auto-rotación (se pausa al pasar el ratón)
  useEffect(() => {
    if (paused) return;
    const id = setInterval(() => {
      setDirection(1);
      setIndex((p) => (p + 1) % DEMO_DATA.length);
    }, AUTOPLAY_MS);
    return () => clearInterval(id);
  }, [paused, index]);

  const current = DEMO_DATA[index];

  return (
    <div
      className={`nc-CommentListing flex w-full flex-col items-center ${className}`}
      data-nc-id="CommentListing"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="flex w-full items-center justify-center gap-2 sm:gap-4">
        <button onClick={prev} aria-label="Reseña anterior" className="hidden h-10 w-10 place-items-center rounded-full text-violet-300 transition hover:bg-white/10 hover:text-violet-200 sm:grid">
          <i className="las la-arrow-left text-3xl" />
        </button>

        <div className="relative flex min-h-[320px] w-full items-center justify-center overflow-hidden px-2 sm:w-[705px]">
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={index}
              custom={direction}
              initial={{ opacity: 0, x: direction * 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: direction * -50 }}
              transition={{ duration: 0.45, ease: "easeOut" }}
              className="flex flex-col items-center text-center"
            >
              <span className="text-2xl font-bold leading-snug text-violet-200 [text-shadow:0_2px_14px_rgba(124,58,237,0.35)] sm:text-3xl md:text-4xl lg:text-4xl">
                {current.comment}
              </span>
              <div className="mt-8 flex flex-col items-center">
                <div className="flex gap-0.5 text-amber-400">
                  {[...Array(current.starPoint)].map((_, i) => (
                    <StarIcon key={i} className="h-5 w-5" />
                  ))}
                </div>
                <span className="mt-3 text-xl font-semibold text-violet-300">{current.name}</span>
                {current.date && <span className="text-sm text-violet-400">{current.date}</span>}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        <button onClick={next} aria-label="Reseña siguiente" className="hidden h-10 w-10 place-items-center rounded-full text-violet-300 transition hover:bg-white/10 hover:text-violet-200 sm:grid">
          <i className="las la-arrow-right text-3xl" />
        </button>
      </div>

      {/* Indicadores */}
      <div className="mt-6 flex items-center gap-2">
        {DEMO_DATA.map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            aria-label={`Ir a la reseña ${i + 1}`}
            className={`h-2 rounded-full transition-all duration-300 ${i === index ? "w-6 bg-violet-400" : "w-2 bg-white/25 hover:bg-white/40"}`}
          />
        ))}
      </div>
    </div>
  );
};

export default CommentListing;
